import { imageFileDataUrl, canShareFortuneImage, sharePreparedFortuneImage } from './fortune-image-file';
import type { FortuneSavePolicy } from './fortune-save-policy';
import { prepareFortuneImageDownload, type FortuneImageDownload } from './fortune-image-download';

export function showFortuneImagePreview(file: File, policy: FortuneSavePolicy, shareFailed: boolean, returnFocusTo?: HTMLElement): void {
  document.querySelector<HTMLDialogElement>('#fortune-save-dialog')?.close();
  const dialog = document.createElement('dialog');
  dialog.id = 'fortune-save-dialog';
  dialog.className = 'fortune-save-dialog';
  dialog.dataset.saveOs = policy.os;
  dialog.dataset.saveBrowser = policy.browser;
  dialog.dataset.saveEngine = policy.engine;
  dialog.dataset.saveTransport = policy.download;
  dialog.setAttribute('aria-labelledby', 'fortune-save-title');
  dialog.setAttribute('aria-describedby', 'fortune-save-description');
  dialog.innerHTML = `
    <div class="fortune-save-heading">
      <h2 id="fortune-save-title">운세 이미지 저장</h2>
      <button class="fortune-save-close" type="button" aria-label="이미지 저장 닫기" autofocus>×</button>
    </div>
    <p id="fortune-save-description"></p>
    <div class="fortune-save-buttons">
      <button class="button button-primary" type="button" data-native-share hidden></button>
      <a class="button button-primary" data-image-download hidden>이미지 다운로드</a>
      <a class="button button-soft" data-image-original target="_blank" rel="noopener noreferrer" hidden>원본 이미지 열기</a>
      <button class="button button-soft" type="button" data-http-prepare>다른 방법으로 저장</button>
    </div>
    <p class="fortune-save-status" role="status" aria-live="polite"></p>
    <img class="fortune-save-image" alt="오늘의 한입 운세 카드" width="1080" height="1080" hidden />
    <p class="fortune-save-help" data-save-help></p>
    <p class="fortune-save-help" data-temporary-help hidden>저장용 이미지만 오늘툴 서버에 최대 10분간 임시 보관해요. 생년월일은 보내지 않아요.</p>`;

  const status = dialog.querySelector<HTMLElement>('.fortune-save-status')!;
  const description = dialog.querySelector<HTMLElement>('#fortune-save-description')!;
  const image = dialog.querySelector<HTMLImageElement>('.fortune-save-image')!;
  const share = dialog.querySelector<HTMLButtonElement>('[data-native-share]')!;
  const download = dialog.querySelector<HTMLAnchorElement>('[data-image-download]')!;
  const original = dialog.querySelector<HTMLAnchorElement>('[data-image-original]')!;
  const prepare = dialog.querySelector<HTMLButtonElement>('[data-http-prepare]')!;
  const help = dialog.querySelector<HTMLElement>('[data-save-help]')!;
  const temporaryHelp = dialog.querySelector<HTMLElement>('[data-temporary-help]')!;
  const controller = new AbortController();
  let objectUrl: string | undefined;
  let httpImage: FortuneImageDownload | undefined;
  let preparingHttp = false;
  let usingHttp = policy.download === 'http';
  const showStatus = (message: string) => { if (!controller.signal.aborted) status.textContent = message; };

  share.hidden = !canShareFortuneImage(file);
  share.textContent = policy.os === 'ios' ? '사진 앱에 저장' : '공유 메뉴 열기';
  download.classList.toggle('button-primary', share.hidden || policy.os !== 'ios');
  download.classList.toggle('button-soft', !share.hidden && policy.os === 'ios');
  download.download = file.name;
  if (policy.os === 'ios') {
    description.textContent = share.hidden
      ? '이미지를 길게 눌러 사진에 저장해 주세요. 메뉴가 없으면 다른 방법으로 저장을 눌러주세요.'
      : '사진 앱에 저장을 누른 뒤 공유 메뉴에서 이미지 저장을 선택해 주세요.';
    help.textContent = '이미지 다운로드는 파일 앱에 저장될 수 있어요. 사진 앱에 넣으려면 이미지 저장을 선택해 주세요.';
  } else {
    description.textContent = '이미지 다운로드를 눌러 운세 카드를 저장해 주세요.';
    help.textContent = '다운로드한 이미지는 갤러리 또는 내 파일의 다운로드 폴더에서 확인할 수 있어요.';
  }

  async function prepareHttpImage(): Promise<void> {
    if (preparingHttp || controller.signal.aborted) return;
    preparingHttp = true;
    usingHttp = true;
    httpImage = undefined;
    download.hidden = true;
    original.hidden = true;
    temporaryHelp.hidden = false;
    prepare.hidden = false;
    prepare.disabled = true;
    prepare.textContent = '저장용 이미지 준비 중…';
    showStatus('이미지를 준비하고 있어요. 완료되면 저장 버튼을 눌러주세요.');
    try {
      const result = await prepareFortuneImageDownload(file, controller.signal);
      if (controller.signal.aborted) return;
      httpImage = result;
      image.src = result.imageUrl;
      image.hidden = false;
      download.href = result.downloadUrl;
      original.href = result.imageUrl;
      download.hidden = false;
      original.hidden = false;
      prepare.hidden = true;
      showStatus('준비됐어요. 이미지 다운로드를 누르거나 원본 이미지를 열어 길게 눌러 저장해 주세요.');
    } catch {
      if (controller.signal.aborted) return;
      prepare.textContent = '저장용 이미지 다시 준비';
      showStatus('저장용 이미지를 준비하지 못했어요. 잠시 후 다시 눌러주세요.');
    } finally {
      preparingHttp = false;
      prepare.disabled = false;
    }
  }

  function guardHttpLink(event: MouseEvent): boolean {
    if (!usingHttp || (httpImage && httpImage.expiresAt > Date.now() + 5_000)) return true;
    event.preventDefault();
    void prepareHttpImage();
    return false;
  }
  download.addEventListener('click', (event) => {
    if (guardHttpLink(event)) showStatus('다운로드를 요청했어요. 저장 위치를 확인해 주세요.');
  });
  original.addEventListener('click', (event) => { guardHttpLink(event); });
  prepare.addEventListener('click', () => { void prepareHttpImage(); });
  share.addEventListener('click', async () => {
    share.disabled = true;
    const mode = await sharePreparedFortuneImage(file);
    share.disabled = false;
    if (mode === 'unavailable') showStatus('사진 저장 메뉴를 열지 못했어요. 아래 이미지를 길게 누르거나 다른 방법으로 저장을 이용해 주세요.');
    else if (mode === 'shared') showStatus('공유 메뉴를 닫았어요. 선택한 저장 위치를 확인해 주세요.');
    else showStatus('');
  });

  dialog.querySelector('button.fortune-save-close')!.addEventListener('click', () => dialog.close());
  dialog.addEventListener('click', (event) => {
    if (event.target !== dialog) return;
    const bounds = dialog.getBoundingClientRect();
    if (event.clientX < bounds.left || event.clientX > bounds.right
      || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
  });
  dialog.addEventListener('close', () => {
    controller.abort();
    dialog.remove();
    // Native download managers may consume the URL after the dialog closes.
    if (objectUrl) window.setTimeout(() => URL.revokeObjectURL(objectUrl!), 60_000);
    if (!document.querySelector('#fortune-save-dialog[open]')) {
      document.documentElement.classList.remove('fortune-save-open');
      if (returnFocusTo?.isConnected) returnFocusTo.focus({ preventScroll: true });
    }
  }, { once: true });
  document.body.append(dialog);
  try { dialog.showModal(); } catch (error) { dialog.remove(); controller.abort(); throw error; }
  document.documentElement.classList.add('fortune-save-open');

  // Conversion only prepares a preview/link; it never delays a native share.
  if (policy.download === 'blob-url') {
    objectUrl = URL.createObjectURL(file);
    image.src = objectUrl;
    image.hidden = false;
    download.href = objectUrl;
    download.hidden = false;
  } else {
    void imageFileDataUrl(file).then((dataUrl) => {
      if (controller.signal.aborted || httpImage) return;
      image.src = dataUrl;
      image.hidden = false;
      if (!usingHttp) {
        download.href = dataUrl;
        download.hidden = false;
      }
    }).catch(() => {
      if (!usingHttp) showStatus('미리보기를 열지 못했어요. 다른 방법으로 저장을 눌러주세요.');
    });
  }
  if (policy.download === 'http') void prepareHttpImage();
  else if (shareFailed) showStatus('사진 저장 메뉴를 열지 못했어요. 사진 앱에 저장을 다시 누르거나 아래에서 다른 방법을 선택해 주세요.');
}
